/**
 * Sentry 전역 SDK 래퍼.
 *
 * SDK 는 번들이 아니라 views/base.pug 의 head 에서 window.Sentry 전역으로
 * async 로드된다(IE11 지원을 위해 ES5 번들을 자체 호스팅). 따라서 계측 코드는
 * 두 상황을 모두 견뎌야 한다.
 *
 * 1. SENTRY_DSN 미설정(dev/local) → window.Sentry 가 영원히 없다.
 *    base.pug 가 세우는 __mbusSentryEnabled 플래그가 없으면 즉시 no-op 한다.
 * 2. 번들이 SDK 보다 먼저 실행 → 번들은 body 끝, SDK 는 head async 이므로
 *    이른 실패(setupError 등)는 SDK 준비 전에 발생할 수 있다. 큐에 담아두고
 *    SDK 가 등장하면 flush 한다.
 *
 * 계측이 재생을 막는 일은 없어야 하므로 모든 호출은 예외를 삼킨다.
 */

export type SentryLevel = 'fatal' | 'error' | 'warning' | 'info';

export interface SentryContext {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  level?: SentryLevel;
}

interface SentryGlobal {
  captureException(error: unknown, context?: SentryContext): void;
  captureMessage(message: string, context?: SentryContext): void;
  setTag(key: string, value: string): void;
}

interface SentryWindow extends Window {
  Sentry?: SentryGlobal;
  __mbusSentryEnabled?: boolean;
}

const MAX_PENDING = 20;
const FLUSH_INTERVAL_MS = 300;
const GIVE_UP_MS = 15000;

const win = () => window as unknown as SentryWindow;

let pending: Array<(sdk: SentryGlobal) => void> = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let waitedMs = 0;

const stopFlushTimer = () => {
  if (flushTimer !== null) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
};

/** 계측 실패가 호출자에게 전파되지 않게 한다. */
const swallow = (fn: () => void) => {
  try {
    fn();
  } catch (e) {
    // 계측 실패는 무시한다. 여기서 다시 Sentry 를 부르면 재귀 위험이 있다.
  }
};

const drain = (sdk: SentryGlobal) => {
  const queued = pending;
  pending = [];
  stopFlushTimer();
  for (let i = 0; i < queued.length; i += 1) {
    const task = queued[i];
    swallow(() => task(sdk));
  }
};

const startFlushTimer = () => {
  if (flushTimer !== null) return;
  waitedMs = 0;
  flushTimer = setInterval(() => {
    const sdk = win().Sentry;
    if (sdk) {
      drain(sdk);
      return;
    }
    waitedMs += FLUSH_INTERVAL_MS;
    if (waitedMs >= GIVE_UP_MS) {
      // SDK 로드 실패(차단기/네트워크). 큐를 비우고 타이머를 정리한다.
      pending = [];
      stopFlushTimer();
    }
  }, FLUSH_INTERVAL_MS);
};

/** SDK 가 준비돼 있으면 즉시, 아니면 큐에 담아 나중에 실행한다. */
const run = (task: (sdk: SentryGlobal) => void) => {
  swallow(() => {
    if (!win().__mbusSentryEnabled) return;

    const sdk = win().Sentry;
    if (sdk) {
      swallow(() => task(sdk));
      return;
    }

    if (pending.length < MAX_PENDING) {
      pending.push(task);
      startFlushTimer();
    }
  });
};

export const isSentryEnabled = (): boolean => !!win().__mbusSentryEnabled;

export const captureError = (error: unknown, context?: SentryContext): void => {
  run((sdk) => sdk.captureException(error, { level: 'error', ...context }));
};

/**
 * 예외 객체가 없는 실패(= 브라우저가 알려주지 않는 실패)를 보고한다.
 * 풀스크린 진입 실패, 자동재생 차단, DRM 키시스템 미검출 등이 여기 해당한다.
 */
export const captureFailure = (
  message: string,
  context?: SentryContext,
): void => {
  run((sdk) => sdk.captureMessage(message, { level: 'warning', ...context }));
};

export const setSentryTags = (tags: Record<string, string>): void => {
  run((sdk) => {
    const keys = Object.keys(tags);
    for (let i = 0; i < keys.length; i += 1) {
      const value = tags[keys[i]];
      if (value) sdk.setTag(keys[i], value);
    }
  });
};

/** 테스트 전용. 모듈 스코프 큐/타이머를 초기화한다. */
export const __resetForTest = (): void => {
  pending = [];
  waitedMs = 0;
  stopFlushTimer();
};
